import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL, formatMeetingDate } from './safety-ui.js';
import type { TerminalOption, SafetySummary } from './use-safety-read-model.js';

const RANGE_PRESETS = [
  { value: 'all',   label: 'All time' },
  { value: '7d',    label: 'Last 7 days' },
  { value: '30d',   label: 'Last 30 days' },
  { value: '90d',   label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

interface SafetyContextRailProps {
  terminalFilter: string;
  terminalOptions: TerminalOption[];
  rangePreset: string;
  startDate: string;
  endDate: string;
  summary: SafetySummary;
  onTerminalChange: (value: string) => void;
  onRangePresetChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onRefresh: () => void;
  onResetFilters: () => void;
}

export default function SafetyContextRail({
  terminalFilter,
  terminalOptions,
  rangePreset,
  startDate,
  endDate,
  summary,
  onTerminalChange,
  onRangePresetChange,
  onStartDateChange,
  onEndDateChange,
  onRefresh,
  onResetFilters,
}: SafetyContextRailProps) {
  const stats = [
    { label: 'Meetings loaded',   value: summary.total },
    { label: 'Open',              value: summary.open,             accent: summary.open > 0 ? C.gold : C.textMuted },
    { label: 'Closed',            value: summary.closed,           accent: C.green },
    { label: 'Signatures',        value: summary.totalSignatures },
    { label: 'Last 30 days',      value: summary.last30d,          accent: summary.last30d > 0 ? C.green : C.textMuted },
    { label: 'Terminals covered', value: summary.terminalsCovered },
  ];

  const isCustom = rangePreset === 'custom';
  const rangeActive = rangePreset !== 'all' || startDate || endDate;

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold }}>
          Safety context rail
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {rangeActive && (
            <button
              type="button"
              onClick={onResetFilters}
              style={{
                background: 'none',
                border: `1px solid ${C.borderSoft}`,
                borderRadius: 6,
                color: C.textMuted,
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Reset filters
            </button>
          )}
          <button
            type="button"
            onClick={onRefresh}
            style={{
              background: 'none',
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 6,
              color: C.textMuted,
              padding: '4px 10px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 12 }}>
        <div>
          <label style={MUTED_LABEL}>Terminal scope</label>
          <select
            value={terminalFilter}
            onChange={(e) => onTerminalChange(e.target.value)}
            style={CONTROL_INPUT}
          >
            {terminalOptions.map((opt: TerminalOption) => (
              <option key={opt.id} value={opt.id}>{opt.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={MUTED_LABEL}>Date range</label>
          <select
            value={rangePreset}
            onChange={(e) => onRangePresetChange(e.target.value)}
            style={CONTROL_INPUT}
          >
            {RANGE_PRESETS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {isCustom ? (
          <>
            <div>
              <label style={MUTED_LABEL}>Start date</label>
              <input
                type="date"
                value={startDate || ''}
                max={endDate || undefined}
                onChange={(e) => onStartDateChange(e.target.value)}
                style={CONTROL_INPUT}
              />
            </div>
            <div>
              <label style={MUTED_LABEL}>End date</label>
              <input
                type="date"
                value={endDate || ''}
                min={startDate || undefined}
                onChange={(e) => onEndDateChange(e.target.value)}
                style={CONTROL_INPUT}
              />
            </div>
          </>
        ) : (
          <div>
            <label style={MUTED_LABEL}>Most recent meeting</label>
            <div style={{ ...CONTROL_INPUT, display: 'flex', alignItems: 'center' }}>
              {summary.latestOpenedAt ? formatMeetingDate(String(summary.latestOpenedAt)) : '—'}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
        {stats.map((stat) => (
          <div key={stat.label} style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
            <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 4 }}>{stat.label}</div>
            <div style={{ color: stat.accent || C.text, fontSize: 18, fontWeight: 700 }}>{stat.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
